import React, { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  Tooltip,
  Collapse,
  Divider,
  TextField,
  InputAdornment,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import InstanceList from './InstanceList';
import SearchIcon from '@mui/icons-material/Search';
import { useSearch } from '../Keyvault/SearchContext';


interface EnvDetails {
  Id: string;                       
  Env_Nm: string;                   
  Env_Desc: string;    
  Env_Typ:string;              
  EnvDisplay_Nm?: string;             
  EnvSrvc_Conn: string;              
  EnvSrvc_Loc: string;               
  EnvRg_Nm: string;                   
  EnvBase_Nm: string;                
  EnvKvlt_Id: string;                 
  EnvSubscrptn_Id: string;             
  EnvStorAcct_Nm: string;     
  EnvCont_Nm: string;          
  EffectiveStart_DtTm: string;       
  EffectiveEnd_DtTm: string;          
  Active_Flg: string;                  
  CreatedBy_Id: string;               
  CreatedBy_DtTm: string;            
  UpdatedBy_Id: string;  
   UpdatedBy_DtTm:string;
}


const EnvDetail: React.FC = () => {
  const { instanceName } = useParams<{ instanceName: string }>();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(true);

  const location = useLocation();
  const envDetails = location.state?.envDetails;
  const { searchText, setSearchText } = useSearch();


  {/*used mapping object so that raw data doesn't shows on UI*/ }
  const labelMapping: Record<string, string> = {
  Id: "ID",
  Env_Nm: "Environment Name",
  Env_Desc: "Environment Description",
  Env_Typ: "Environment Type",
  EnvDisplay_Nm: "Environment Display Name",
  EnvSrvc_Conn: "Service Connection",
  EnvSrvc_Loc: "Service Location",
  EnvRg_Nm: "Resource Group",
  EnvBase_Nm: "Base Environment",
  EnvKvlt_Id: "Key Vault ID",
  EnvSubscrptn_Id: "Subscription ID",
  EnvStorAcct_Nm: "Storage Account Name",
  EnvCont_Nm: "Container Name",
  EffectiveStart_DtTm: "Effective Start (Date/Time)",
  EffectiveEnd_DtTm: "Effective End (Date/Time)",
  Active_Flg: "Active Flag",
  CreatedBy_Id: "Created By",
  CreatedBy_DtTm: "Created On (Date/Time)",
  UpdatedBy_Id: "Updated By",
  UpdatedBy_DtTm: "Updated On (Date/Time)",
  };

  return (
    <Box
      sx={{
        bgcolor: '#f5f5f5',
        overflowY: 'auto',
        p: 4,
      }}
    >
      {/* Search Bar */}
      <Box display="flex" justifyContent="center" mb={3}>
        <TextField
          variant="outlined"
          placeholder="Search instances..."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            style: {
              background: 'white',
              borderRadius: 8,
              minWidth: 300,
              width: '100%',
              maxWidth: 500,
            },
          }}
        />
      </Box>

      {/* Header */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={() => navigate('/TenantDetails')} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" sx={{ color: '#734bd1', fontWeight: 'bold' }}>
          Env Name: {instanceName}
        </Typography>
      </Box>

      {/* Collapsible Details Card */}
      <Card sx={{ mb: 4 }}>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          px={2}
          py={1}
          sx={{ backgroundColor: '#e0d3ff', cursor: 'pointer' }}
          onClick={() => setCollapsed(!collapsed)}
        >
          <Typography variant="subtitle1" >
            Environment Details
          </Typography>
          <IconButton size="small">
            {collapsed ? <ExpandMoreIcon /> : <ExpandLessIcon />}
          </IconButton>
        </Box>

        <Collapse in={collapsed} timeout="auto" unmountOnExit>
          <Divider />
          <CardContent>
            <Grid container spacing={2}>
              {envDetails &&
                Object.entries(envDetails).map(([key, value]) => {
                  const displayValue = (value ?? '').toString();
                  return (
                    <Grid item xs={12} sm={6} key={key}>
                      <Box display="flex" gap={1}>
                        <Typography
                          variant="subtitle2"
                          sx={{ color: '#734bd1', minWidth: 200 }}
                        >
                          {labelMapping[key] || key}
                        </Typography>
                        <Typography sx={{ color: '#734bd1', minWidth: 20 }}>
                          :
                        </Typography>
                        <Tooltip title={displayValue} placement="top-start" arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              color: '#333',
                              maxWidth: 'calc(100% - 160px)',
                            }}
                          >
                            {displayValue}
                          </Typography>
                        </Tooltip>
                      </Box>
                    </Grid>
                  );
                })}
            </Grid>
          </CardContent>
        </Collapse>
      </Card>

      {/* Instance List Table */}
      <InstanceList envId={envDetails?.Id} />
    </Box>
  );
};

export default EnvDetail;
